import React from 'react'
import { Table, TableColumnsType } from 'antd'
import { IMovementRS } from '../../../models/salesModel'
import { textNumberFormater } from '../../../utils'
import { groupSalesByPaymentMethod, calculateSummary, TGroupedData } from '../../../utils/reportUtils'
import { TExcelColumn } from '../../../utils/excelExport'
import ReportSummaryCards, { TSummaryCard } from './ReportSummaryCards'
import { useRegister } from '../../../contexts/RegisterContext'
import {
  DollarOutlined,
  CreditCardOutlined,
  WalletOutlined,
  RiseOutlined
} from '@ant-design/icons'

type SalesByPaymentReportProps = {
  data: IMovementRS[]
  loading?: boolean
}

function SalesByPaymentReport({ data, loading }: Readonly<SalesByPaymentReportProps>) {
  const { register } = useRegister()
  const grouped = groupSalesByPaymentMethod(data, register.paymentOptions)
  const summary = calculateSummary(data)
  const topMethod = grouped[0]

  const cards: TSummaryCard[] = [
    {
      title: 'Total Recaudado',
      value: summary.totalAmount,
      format: 'money',
      color: '#52c41a',
      prefix: <DollarOutlined />
    },
    {
      title: 'Métodos Utilizados',
      value: grouped.length,
      format: 'number',
      color: '#1890ff',
      prefix: <CreditCardOutlined />
    },
    {
      title: 'Método Principal',
      value: topMethod?.label || '-',
      format: 'text',
      color: '#faad14',
      prefix: <WalletOutlined />
    },
    {
      title: 'Transacciones',
      value: summary.totalSales,
      format: 'number',
      color: '#722ed1',
      prefix: <RiseOutlined />
    }
  ]

  const columns: TableColumnsType = [
    { title: '#', dataIndex: 'rank', width: 50 },
    { title: 'Método de Pago', dataIndex: 'label', width: 200 },
    {
      title: 'Transacciones',
      dataIndex: 'count',
      width: 120,
      align: 'center',
      sorter: (a: any, b: any) => a.count - b.count
    },
    {
      title: 'Monto Total',
      dataIndex: 'totalAmount',
      width: 160,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table" style={{ fontWeight: 600 }}>{textNumberFormater(text)}</span>,
      sorter: (a: any, b: any) => a.totalAmount - b.totalAmount,
      defaultSortOrder: 'descend'
    },
    {
      title: '% Participación',
      dataIndex: 'percentage',
      width: 130,
      align: 'center',
      render: (text) => `${text}%`,
      sorter: (a: any, b: any) => a.percentage - b.percentage
    }
  ]

  const tableData = grouped.map((el, i) => ({
    ...el,
    rank: i + 1,
    key: el.key
  }))

  return (
    <div>
      <ReportSummaryCards cards={cards} loading={loading} />
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 600 }}
        size="small"
        loading={loading}
        summary={() => {
          if (tableData.length === 0) return null
          const totalTransactions = tableData.reduce((acc, el) => acc + el.count, 0)
          const totalAmount = tableData.reduce((acc, el) => acc + el.totalAmount, 0)
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={2}>TOTALES</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">{totalTransactions}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span className="numeric-values-data-table" style={{ fontWeight: 700, color: '#52c41a' }}>
                    {textNumberFormater(totalAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="center">100%</Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )
        }}
      />
    </div>
  )
}

export const paymentExcelColumns: TExcelColumn[] = [
  { header: '#', key: 'rank', width: 6 },
  { header: 'Método de Pago', key: 'label', width: 25 },
  { header: 'Transacciones', key: 'count', width: 15, format: 'number' },
  { header: 'Monto Total', key: 'totalAmount', width: 18, format: 'money' },
  { header: '% Participación', key: 'percentage', width: 15, format: 'percent' }
]

export function getPaymentExcelData(data: IMovementRS[], paymentOptions?: any[]): (TGroupedData & { rank: number })[] {
  return groupSalesByPaymentMethod(data, paymentOptions).map((el, i) => ({
    ...el,
    rank: i + 1
  }))
}

export default SalesByPaymentReport
